import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, ExternalLink, BookOpen, Plus } from "lucide-react";

export default function CPDPage() {
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [my, setMy] = useState({ logs: [], total_hours: 0, ytd_hours: 0 });
  const [f, setF] = useState({ resource_id: "", custom_title: "", custom_provider: "", hours: 1, reflection: "", completed_on: new Date().toISOString().slice(0, 10) });
  const [show, setShow] = useState(false);
  const [providerFilter, setProviderFilter] = useState("all");

  const refresh = async () => {
    const [r, m] = await Promise.all([api.get("/cpd/resources"), api.get("/cpd/logs/mine")]);
    setResources(r.data); setMy(m.data);
  };
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.reflection || !f.hours) return toast.error("Add hours and reflection");
    try {
      await api.post("/cpd/logs", { ...f, hours: parseFloat(f.hours) });
      toast.success("CPD logged");
      setF({ resource_id: "", custom_title: "", custom_provider: "", hours: 1, reflection: "", completed_on: new Date().toISOString().slice(0, 10) });
      setShow(false); refresh();
    } catch { toast.error("Failed"); }
  };

  const providers = ["all", ...Array.from(new Set(resources.map((r) => r.provider)))];
  const filtered = providerFilter === "all" ? resources : resources.filter((r) => r.provider === providerFilter);

  return (
    <DashLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">{t("pages.cpd.eyebrow")}</div>
        <h1 className="fl-heading text-4xl mt-2">{t("pages.cpd.h1")}</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="fl-card p-6">
          <GraduationCap className="text-[#f47a20]" size={18} />
          <div className="fl-label mt-3">{t("pages.cpd.statTotal")}</div>
          <div className="fl-heading text-4xl mt-1 text-[#f47a20]">{my.total_hours}</div>
        </div>
        <div className="fl-card p-6">
          <BookOpen className="text-[#f47a20]" size={18} />
          <div className="fl-label mt-3">{t("pages.cpd.statYtd")}</div>
          <div className="fl-heading text-4xl mt-1">{my.ytd_hours}</div>
        </div>
        <div className="fl-card p-6 flex flex-col justify-between">
          <div>
            <div className="fl-label">Log CPD</div>
            <div className="text-sm text-zinc-400 mt-1">From a curated resource or a custom course/paper.</div>
          </div>
          <button onClick={() => setShow(!show)} className="fl-btn mt-3 self-start" data-testid="cpd-log-btn"><Plus size={14} /> New log</button>
        </div>
      </div>

      {show && (
        <form onSubmit={submit} className="fl-card p-6 mb-6 grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <div className="fl-label mb-1">Pick a curated resource (optional)</div>
            <select className="fl-input" value={f.resource_id} onChange={(e) => setF({ ...f, resource_id: e.target.value })} data-testid="cpd-resource">
              <option value="">— custom entry —</option>
              {resources.map((r) => <option key={r.id} value={r.id}>{r.provider} · {r.title}</option>)}
            </select>
          </div>
          {!f.resource_id && (<>
            <input placeholder="Custom title" className="fl-input" value={f.custom_title} onChange={(e) => setF({ ...f, custom_title: e.target.value })} />
            <input placeholder="Provider" className="fl-input" value={f.custom_provider} onChange={(e) => setF({ ...f, custom_provider: e.target.value })} />
          </>)}
          <input type="number" step="0.25" placeholder="Hours" className="fl-input" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="cpd-hours" />
          <input type="date" className="fl-input" value={f.completed_on} onChange={(e) => setF({ ...f, completed_on: e.target.value })} data-testid="cpd-date" />
          <textarea placeholder="Reflection — what did you learn? How will it change practice?" className="fl-input md:col-span-2" rows={4} value={f.reflection} onChange={(e) => setF({ ...f, reflection: e.target.value })} data-testid="cpd-reflection" />
          <button className="fl-btn md:col-span-2 justify-center" data-testid="cpd-save">Save log</button>
        </form>
      )}

      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {providers.map((p) => (
            <button key={p} onClick={() => setProviderFilter(p)} className={`px-4 py-1.5 rounded-md text-xs uppercase tracking-wider border ${providerFilter === p ? "border-[#f47a20] text-[#f47a20]" : "border-white/10 text-zinc-400"}`} data-testid={`cpd-filter-${p}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {filtered.map((r) => (
          <div key={r.id} className="fl-card p-5 hover:border-[#f47a20]/40 transition-colors" data-testid={`cpd-res-${r.id}`}>
            <div className="flex justify-between">
              <div className="fl-label text-[#f47a20]">{r.provider}</div>
              <div className="fl-label">{r.hours}h · {r.category}</div>
            </div>
            <div className="fl-heading text-lg mt-2">{r.title}</div>
            {r.description && <div className="text-sm text-zinc-400 mt-1">{r.description}</div>}
            {r.url && (
              <a href={r.url} target="_blank" rel="noreferrer" className="fl-btn-ghost text-xs mt-4 inline-flex items-center gap-2">
                Open at {r.provider} <ExternalLink size={12} />
              </a>
            )}
          </div>
        ))}
      </div>

      <div className="fl-card p-6">
        <div className="fl-heading text-xl mb-4">My CPD log</div>
        {my.logs.length === 0 && <div className="text-sm text-zinc-500">Nothing logged yet.</div>}
        <div className="space-y-4">
          {my.logs.map((l) => (
            <div key={l.id} className="border-l-2 border-[#f47a20]/40 pl-4">
              <div className="flex justify-between">
                <div className="fl-heading text-base">{l.resource_title || l.custom_title || "Custom entry"}</div>
                <div className="fl-label">{l.hours}h · {l.completed_on}</div>
              </div>
              <div className="text-xs text-zinc-500">{l.resource_provider || l.custom_provider}</div>
              <div className="text-sm text-zinc-300 mt-2">{l.reflection}</div>
            </div>
          ))}
        </div>
      </div>
    </DashLayout>
  );
}
